import type {
  PickupAttemptStatus,
  PickupOpportunity,
  PickupOpportunityAttempt
} from '@/types/pickupOpportunity';
import type {
  PickupAnalysisOpportunityAttempt,
  PickupEligibleProduct,
  PickupLoan,
  PricingWaterfall
} from './types';
import { normalizePickupReconciliationInvestor } from './pickupReconciliationPolicy';
import { pickupDeltaPoints, pickupPointsToDollars } from './pickupPricingMath';

export interface PickupInvestorPricingContext {
  lockedPrice: number | null;
  lockedInvestorPrice: number | null;
  currentInvestorPrice: number | null;
  currentInvestor: string | null;
  loanAmount: number | null;
  analyzedAt: string | null;
  basisLockPeriod: number | null;
  eligibleProducts: PickupEligibleProduct[];
  pricingWaterfall: PricingWaterfall | null;
  opportunityAttempts: PickupAnalysisOpportunityAttempt[];
}

export interface PickupInvestorPricingRow {
  product: PickupEligibleProduct;
  investor: string;
  productName: string;
  price: number;
  deltaPoints: number | null;
  deltaDollars: number | null;
  rank: number | null;
  beatsLock: boolean;
  currentInvestor: boolean;
  attemptStatus: PickupAttemptStatus | null;
}

function finitePrice(value: number | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  const price = Number(value);
  return Number.isFinite(price) ? price : null;
}

export function pickupInvestorPricingInvestorCount(
  rows: PickupInvestorPricingRow[]
): number {
  return new Set(rows.map((row) => (
    normalizePickupReconciliationInvestor(row.investor) || row.investor.toLowerCase()
  ))).size;
}

export function pickupInvestorPricingContext(
  loan: PickupLoan,
  opportunityAttempts: PickupAnalysisOpportunityAttempt[]
): PickupInvestorPricingContext {
  return {
    lockedPrice: loan.locked_price,
    lockedInvestorPrice: loan.locked_investor_price,
    currentInvestorPrice: loan.current_investor_price,
    currentInvestor: loan.ob_investor_name || loan.investor_name,
    loanAmount: loan.base_loan_amount || loan.trans_details_loan_amt,
    analyzedAt: loan.analyzed_at,
    basisLockPeriod: loan.basis_lock_period,
    eligibleProducts: loan.eligible_products || [],
    pricingWaterfall: loan.pricing_waterfall,
    opportunityAttempts
  };
}

export function pickupInvestorPricingContextFromOpportunity(
  opportunity: PickupOpportunity,
  evidence: {
    eligible_products: PickupEligibleProduct[] | null;
    pricing_waterfall: PricingWaterfall | null;
  } | null,
  attempts: PickupOpportunityAttempt[]
): PickupInvestorPricingContext {
  return {
    lockedPrice: opportunity.original_lock_price,
    lockedInvestorPrice: evidence?.pricing_waterfall?.investorPrice ?? null,
    currentInvestorPrice: opportunity.latest_current_investor_price,
    currentInvestor: opportunity.original_investor,
    loanAmount: opportunity.loan_amount,
    analyzedAt: opportunity.latest_priced_at,
    basisLockPeriod: opportunity.basis_lock_period,
    eligibleProducts: evidence?.eligible_products || [],
    pricingWaterfall: evidence?.pricing_waterfall || null,
    opportunityAttempts: attempts.map((attempt) => ({
      id: attempt.id,
      opportunityId: attempt.opportunity_id,
      obLoanId: opportunity.ob_loan_id,
      investorName: attempt.investor_name,
      status: attempt.status
    }))
  };
}

export function buildPickupInvestorPricingRows(
  context: PickupInvestorPricingContext
): PickupInvestorPricingRow[] {
  const attemptsByInvestor = new Map<string, PickupAttemptStatus>();
  for (const attempt of context.opportunityAttempts) {
    const key = normalizePickupReconciliationInvestor(attempt.investorName);
    if (key && !attemptsByInvestor.has(key)) {
      attemptsByInvestor.set(key, attempt.status);
    }
  }
  const currentInvestorKey = normalizePickupReconciliationInvestor(context.currentInvestor);
  let nextRank = 0;

  return context.eligibleProducts
    .flatMap((product) => {
      const price = finitePrice(product.price);
      if (price === null) {
        return [];
      }
      return [{ product, price }];
    })
    .sort((left, right) => (
      right.price - left.price
      || String(left.product.investor || '').localeCompare(String(right.product.investor || ''))
      || String(left.product.productName || '').localeCompare(String(right.product.productName || ''))
    ))
    .map(({ product, price }) => {
      const deltaPoints = pickupDeltaPoints(price, context.lockedPrice);
      const beatsLock = deltaPoints !== null && deltaPoints > 0;
      const investor = String(product.investor || '').trim() || 'Unknown investor';
      const investorKey = normalizePickupReconciliationInvestor(investor);
      const currentInvestor = Boolean(
        investorKey && currentInvestorKey && investorKey === currentInvestorKey
      );
      return {
        product,
        investor,
        productName: String(product.productName || '').trim() || 'Product unavailable',
        price,
        deltaPoints,
        deltaDollars: pickupPointsToDollars(deltaPoints, context.loanAmount),
        rank: beatsLock ? ++nextRank : null,
        beatsLock,
        currentInvestor,
        attemptStatus: investorKey ? attemptsByInvestor.get(investorKey) ?? null : null
      };
    });
}
